import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardSidebar';
import api from '../services/api';

// Files are stored on the admin server, not the website server
const UPLOADS_BASE = process.env.REACT_APP_UPLOADS_BASE_URL || 'http://localhost:5011/uploads';

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
};

const StudentStudyMaterialsPage = () => {
  const [materials, setMaterials] = useState([]);
  const [courses, setCourses]     = useState([]);
  const [topics, setTopics]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedTopic, setSelectedTopic]   = useState('');

  const [viewItem, setViewItem]   = useState(null);  // for text content viewer

  // Load courses from subscriptions
  useEffect(() => {
    api.get('/users/subscriptions').then(res => {
      const subs = res.data.data || [];
      const map = new Map();
      subs.forEach(s => { if (!map.has(s.CourseId)) map.set(s.CourseId, { CourseId: s.CourseId, CourseName: s.CourseName || s.PackTitle }); });
      setCourses(Array.from(map.values()));
    }).catch(() => {});
  }, []);

  // Load topics when course changes
  useEffect(() => {
    if (!selectedCourse) { setTopics([]); return; }
    api.get(`/study-materials?courseId=${selectedCourse}&limit=100`).then(res => {
      if (res.data.success) {
        const topicMap = new Map();
        res.data.data.forEach(m => {
          if (m.TopicId && m.TopicName && !topicMap.has(m.TopicId)) topicMap.set(m.TopicId, m.TopicName);
        });
        setTopics(Array.from(topicMap, ([id, name]) => ({ TopicId: id, TopicName: name })));
      }
    }).catch(() => {});
  }, [selectedCourse]);

  // Load materials
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedCourse) params.set('courseId', selectedCourse);
    if (selectedTopic)  params.set('topicId', selectedTopic);
    params.set('limit', '50');

    api.get(`/study-materials?${params}`).then(res => {
      if (res.data.success) setMaterials(res.data.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedCourse, selectedTopic]);

  const handleDownload = async (item) => {
    try {
      // Track download
      await api.get(`/study-materials/${item.MaterialId}`);
    } catch {} // silent — download link works regardless
    window.open(`${UPLOADS_BASE}/${item.FilePath}`, '_blank');
  };

  const handleViewText = async (item) => {
    try {
      const res = await api.get(`/study-materials/${item.MaterialId}`);
      if (res.data.success) setViewItem(res.data.data);
    } catch {
      alert('Failed to load content');
    }
  };

  return (
    <DashboardLayout title="Study Materials">
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3"><span className="text-4xl">📚</span> Study Materials</h1>
            <p className="text-text-muted mt-1">Download PDFs and read study content for your courses</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10 mb-8 flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-text-muted text-sm mb-2">Course</label>
            <select className="w-full bg-[#2d1b4e] border border-white/20 rounded-lg p-3 text-white outline-none cursor-pointer"
              value={selectedCourse} onChange={e => { setSelectedCourse(e.target.value); setSelectedTopic(''); }}>
              <option value="">All Courses</option>
              {courses.map(c => <option key={c.CourseId} value={c.CourseId}>{c.CourseName}</option>)}
            </select>
          </div>
          {topics.length > 0 && (
            <div className="flex-1 min-w-[180px]">
              <label className="block text-text-muted text-sm mb-2">Subject</label>
              <select className="w-full bg-[#2d1b4e] border border-white/20 rounded-lg p-3 text-white outline-none cursor-pointer"
                value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)}>
                <option value="">All Subjects</option>
                {topics.map(t => <option key={t.TopicId} value={t.TopicId}>{t.TopicName}</option>)}
              </select>
            </div>
          )}
          <div className="text-sm text-text-muted">{materials.length} material{materials.length !== 1 ? 's' : ''} found</div>
        </div>

        {/* Materials Grid */}
        {loading ? (
          <div className="text-center py-20 text-text-muted animate-pulse">Loading materials...</div>
        ) : materials.length === 0 ? (
          <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
            <div className="text-5xl mb-4">📚</div>
            <p className="text-text-muted">No study materials available{selectedCourse ? ' for this course' : ''}.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {materials.map(m => (
              <div key={m.MaterialId} className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-accent-gold/30 transition-all group">
                {/* Type badge */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs px-2 py-1 rounded font-semibold ${m.ContentType === 'pdf' ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'}`}>
                    {m.ContentType === 'pdf' ? '📄 PDF' : '📝 Text'}
                  </span>
                  {m.TopicName && <span className="text-xs text-text-muted bg-white/10 px-2 py-1 rounded">{m.TopicName}</span>}
                </div>

                {/* Title */}
                <h3 className="font-bold text-text-light mb-2 line-clamp-2 group-hover:text-accent-gold transition-colors">{m.Title}</h3>
                {m.Description && <p className="text-text-muted text-sm line-clamp-2 mb-4">{m.Description}</p>}

                {/* Meta */}
                <div className="flex items-center gap-3 text-xs text-text-muted mb-4">
                  <span>{m.CourseName}</span>
                  {m.FileSize && <span>• {formatBytes(m.FileSize)}</span>}
                </div>

                {/* Action */}
                {m.ContentType === 'pdf' ? (
                  <button onClick={() => handleDownload(m)}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-purple-500/20 transition-all flex items-center justify-center gap-2">
                    📥 Download PDF
                  </button>
                ) : (
                  <button onClick={() => handleViewText(m)}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-blue-500/20 transition-all flex items-center justify-center gap-2">
                    📖 Read Content
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Text Content Viewer Modal */}
        {viewItem && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a0b2e] border border-white/20 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl">
              <div className="flex justify-between items-center p-6 border-b border-white/10">
                <div>
                  <h3 className="text-xl font-bold text-white">{viewItem.Title}</h3>
                  <p className="text-text-muted text-sm mt-1">{viewItem.CourseName}{viewItem.TopicName ? ` • ${viewItem.TopicName}` : ''}</p>
                </div>
                <button onClick={() => setViewItem(null)} className="text-white/50 hover:text-white text-2xl">×</button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="prose prose-invert max-w-none text-text-light leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: viewItem.TextContent }} />
              </div>
              <div className="p-4 border-t border-white/10 flex justify-end">
                <button onClick={() => setViewItem(null)} className="px-6 py-2 border border-white/20 rounded-xl text-white hover:bg-white/5">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StudentStudyMaterialsPage;